import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeftRight, ChevronDown } from 'lucide-react';
import { useAuth } from '../services/AuthContext';

/**
 * Switch hats. Owner, Sep 18 2026: one login for Michael that moves between
 * the field and the admin side. Second pass, same day: "bidder, inspector and
 * employee all in one, so it shows all my stops — then I just switch from
 * that to admin when I need to." So for a login with an admin hat the field
 * side is one choice, Field (the admin app's own page); from the Field page
 * the menu offers the admin side back. Shows only when there is somewhere
 * else to go.
 */
const ADMIN_HATS = ['admin', 'manager'];
const word = (r: string) => r === 'crew' ? 'Employee' : r.charAt(0).toUpperCase() + r.slice(1);

export default function RoleSwitcher({ tone = 'light', compact = false, up = false }: { tone?: 'light' | 'dark'; compact?: boolean; up?: boolean }) {
  const { user, switchRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const choices = (user as any)?.choices;
  const current: string = choices?.current || '';
  // On the Field page the admin side is the way back — the hat already worn included.
  const inField = ADMIN_HATS.includes(current) && location.pathname.startsWith('/field');
  // The commercial side (owner, Sep 28 2026) is the admin hat on its own pages, like Field.
  const inCommercial = ADMIN_HATS.includes(current) && location.pathname.startsWith('/commercial');
  // The owner's Employee hat (the crew app, worn as a crew member) stays on offer from the Field page.
  const fieldChoices: any[] = (choices?.field || []).filter((c: any) => !(inField && c.home === '/field'));
  const commercialChoices: any[] = inCommercial ? [] : (choices?.commercial || []);
  const adminChoices: any[] = (inField || inCommercial) ? [{ role: current, label: word(current), home: '/my-day' }, ...(choices?.admin || [])] : (choices?.admin || []);
  if (!choices || (!fieldChoices.length && !adminChoices.length && !commercialChoices.length)) return null;

  async function go(c: { role: string; label: string; home: string }) {
    setBusy(true);
    try {
      // The Field page is served by the admin hat: switch only when that is not the hat on.
      let home = c.home;
      if (c.role !== current) { const h = await switchRole(c.role); home = c.home || h; }
      setOpen(false);
      toast.success(c.label === 'Field' ? 'Now in the field' : c.label === 'Commercial' ? 'Now on the commercial side' : `Now as ${c.label}`);
      navigate(home, { replace: true });
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Could not switch'); }
    finally { setBusy(false); }
  }

  const btn = tone === 'dark'
    ? 'text-brand-100 hover:text-white border border-white/20 dark:text-snow/85 dark:hover:text-snow dark:border-snow/20'
    : 'text-gray-900 border border-gray-200 bg-white hover:border-brand-300';
  const currentLabel = inField ? 'Field' : inCommercial ? 'Commercial' : word(current);

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(o => !o)} className={`press flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-xs font-bold shadow-soft ${btn}`} title="Switch hats">
        <ArrowLeftRight className="h-3.5 w-3.5" />{compact ? currentLabel : `${currentLabel} · switch`}<ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className={`absolute z-50 ${up ? 'bottom-full mb-1 left-0' : 'mt-1 right-0'} w-56 rounded-2xl border border-gray-200 bg-white shadow-lift p-1.5 text-gray-900`}>
            {fieldChoices.length > 0 && (
              <div>
                <p className="px-2 pt-1 pb-0.5 text-[11px] font-black uppercase tracking-wide text-gray-600">Out in the field</p>
                {fieldChoices.map((c: any) => (
                  <button key={c.label} type="button" disabled={busy} onClick={() => go(c)} className="press w-full text-left px-2.5 py-2.5 rounded-xl text-sm font-semibold hover:bg-brand-50 disabled:opacity-50">{c.label}</button>
                ))}
              </div>
            )}
            {commercialChoices.length > 0 && (
              <div>
                <p className="px-2 pt-1 pb-0.5 text-[11px] font-black uppercase tracking-wide text-gray-600">Commercial side</p>
                {commercialChoices.map((c: any) => (
                  <button key={c.label} type="button" disabled={busy} onClick={() => go(c)} className="press w-full text-left px-2.5 py-2.5 rounded-xl text-sm font-semibold text-gray-900 hover:bg-brand-50 disabled:opacity-50">{c.label}</button>
                ))}
              </div>
            )}
            {adminChoices.length > 0 && (
              <div>
                <p className="px-2 pt-1 pb-0.5 text-[11px] font-black uppercase tracking-wide text-gray-600">Admin side</p>
                {adminChoices.map((c: any) => (
                  <button key={c.label} type="button" disabled={busy} onClick={() => go(c)} className="press w-full text-left px-2.5 py-2.5 rounded-xl text-sm font-semibold hover:bg-brand-50 disabled:opacity-50">{c.label}</button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
